import { Button } from '@thanx/uikit/button'
import { Switch } from '@thanx/uikit/switch'
import { getApp } from 'actions/app'
import Help from 'components/Help'
import Formsy from 'formsy-react'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import isEmpty from 'lodash/isEmpty'
import { Fields as Location } from 'models/Location'
import { RedemptionProvider } from 'models/RedemptionProvider'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap-five'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import EditableName from 'scenes/CampaignCenter/Builder/components/EditableName'
import {
  getUniqueRedemptionProviders,
  isFreeVariantKey,
} from 'scenes/RedeemManager/helpers'
import ChangeActiveTemplateModal from 'scenes/RedeemManager/Modals/ChangeActiveTemplateModal'
import SendTestModal from 'scenes/RedeemManager/Modals/SendTestModal'
import { selectCouponPoolsByTemplateId } from 'selectors/couponPool'
import { selectUploadsByCouponPoolId } from 'selectors/fileUpload'
import { useAbility } from 'utilities/ability'
import { useIsMobile } from 'utilities/responsive'
import ActivateModal from '../../Modals/ActivateModal'
import { FormModel, useFormModel } from '../FormModel'
import { Step } from '../index'
import Success from '../Success'
import TopBar from '../TopBar'
import AppearanceCard from './AppearanceCard'
import CustomerSupportCard from './CustomerSupportCard'
import DeliveryDiscountCard from './DeliveryDiscountCard'
import DescriptionCard from './DescriptionCard'
import DiscountCard from './DiscountCard'
import FixedPriceCard from './FixedPriceCard'
import { getHelp } from './helpers'
import InstoreRedemptionCard from './InstoreRedemptionCard'
import ItemValueCard from './ItemValueCard'
import MaximumDiscountCard from './MaximumDiscountCard'
import MinimumSpendCard from './MinimumSpendCard'
import MultipleRedemptionProviders from './MultipleRedemptionProviders'
import LocationPosModal from './MultipleRedemptionProviders/LocationPosModal'
import MultiUseRewardsCard from './MultiUseRewardsCard'
import OnlineRedemptionCard from './OnlineRedemptionCard'
import Preview from './Preview'
import Restrictions from './Restrictions'

export enum Card {
  BONUS_POINTS,
  REWARD_DESCRIPTION,
  REWARD_APPEARANCE,
  ITEM_VALUE,
  DISCOUNT_CONFIG,
  ONLINE_REDEMPTION,
  INSTORE_REDEMPTION,
  MINIMUM_SPEND,
  MAXIMUM_DISCOUNT,
  MULTI_USE_REWARDS,
  CUSTOMER_SUPPORT,
}

type Props = {
  templateId: number
  template?: any
  isLoading: boolean
  isActive: boolean
  isDraft: boolean
  isValidForm: boolean
  setIsValidForm: (isValid: boolean) => void
  isValidCouponPool: boolean
  setIsValidCouponPool: (isValid: boolean) => void
  isDirty: boolean
  onStepSelected: (step: Step) => void
  onFormChange: (newFormModel: FormModel, isChanged: boolean) => void
  onSuccessExit: () => void
  saveForm: () => Promise<boolean>
  locations: Location[]
  setConfigureProviderStep: (configureProviderStep: boolean) => void
  setSelectedProvider: (redemptionProvider: RedemptionProvider) => void
}

const t = buildTranslate('redeem_manager.builder.steps.configuration')

const ConfigurationStep: React.FC<Props> = ({
  templateId,
  template,
  isLoading,
  isActive,
  isDraft,
  isValidForm,
  setIsValidForm,
  isValidCouponPool,
  setIsValidCouponPool,
  isDirty,
  onStepSelected,
  onFormChange,
  onSuccessExit,
  saveForm,
  locations,
  setConfigureProviderStep,
  setSelectedProvider,
}) => {
  const formModel = useFormModel().model

  // Check if multi-use settings have changed
  const hasMultiUseChanges = React.useMemo(() => {
    if (!template?.multi_use_settings || !formModel.multiUseRewards) {
      return false
    }

    const original = template.multi_use_settings
    const current = formModel.multiUseRewards

    // Check if usage type changed
    const originalIsMultiUse =
      original.schema ===
      'Program::Issuance::MultiUseStrategies::EveryXDaysStrategy'
    const currentIsMultiUse = current.usageType === 'multiple_times'

    if (originalIsMultiUse !== currentIsMultiUse) return true

    // If both are multi-use, compare specific fields
    if (currentIsMultiUse && originalIsMultiUse) {
      const originalEndCondition = original.end_condition || {}
      const originalLimitCondition = original.limit_condition || {}

      return (
        originalEndCondition.active_window_duration !==
          (current.activeWindowDuration
            ? current.activeWindowDuration * 24 * 60 * 60
            : undefined) ||
        originalEndCondition.max_issuances !== current.maxIssuances ||
        originalLimitCondition.max_issuances_per_window !==
          current.maxIssuancesPerWindow ||
        originalLimitCondition.window_duration !==
          (current.windowDuration
            ? current.windowDuration * 24 * 60 * 60
            : undefined)
      )
    }

    return false
  }, [template, formModel.multiUseRewards])

  const {
    typeItem,
    name,
    description,
    availableOnline,
    availableInstore,
    availableCustomerSupport,
    discountedProductIds,
    requiredProductIds,
    instoreRedemptionType,
    couponCodes,
    hiddenMenuKey,
    longDescription,
    selectedCouponPools,
  } = formModel
  const isMobile = useIsMobile()
  const merchant = useCurrentMerchant()
  const ability = useAbility()
  const canAccessFeedback =
    useFlag('role-based-access-control', false) ||
    ability.can('schedule', 'Campaign')
  const subtype = typeItem?.subtype
  const key = typeItem?.key
  const isForWholePurchase = subtype === 'purchase'
  const dispatch = useDispatch()
  const formRef = React.useRef() as React.MutableRefObject<HTMLFormElement>
  const isProcessingChange = useRef(false)

  // Loading app so that the reward icon and colors exist
  useEffect(() => {
    dispatch(getApp())
  }, [dispatch])

  const couponPools = useSelector(state =>
    selectCouponPoolsByTemplateId(state, templateId)
  )
  const fileUploads = useSelector(state => {
    return !!couponPools?.length
      ? selectUploadsByCouponPoolId(state, couponPools?.[0].id)
      : []
  })
  const isHiddenMenu = key === 'hidden_menu'
  const isGoldenTicket = key === 'golden_ticket'
  const isExperience = isHiddenMenu || isGoldenTicket
  // $-off templates have no `maximum` field; surface min only. Guard on
  // length so an empty types array doesn't make every() vacuously true.
  const isDollarOff =
    !!typeItem?.types?.length &&
    typeItem.types.every(t => t.endsWith('/amount'))
  const isDeliveryDiscount = key === 'delivery_discount'

  const locationSpecificPosEnabled = useFlag('provider-redemption', false)
  const freeModifierRewardsEnabled = useFlag('mx-free-modifier-rewards', false)
  const multiUseRewardsEnabled = useFlag('multi_use_rewards_ui', false)
  const templateMinMaxEnabled = useFlag('mx-template-min-max', false)
  const isBonusPoints = key === 'bonus_points'
  const showMinimumSpend =
    isDeliveryDiscount ||
    (templateMinMaxEnabled && !isExperience && !isBonusPoints)
  const showMaximumDiscount =
    templateMinMaxEnabled && !isExperience && !isBonusPoints && !isDollarOff
  const multiplePosSystems =
    locationSpecificPosEnabled && merchant?.has_multi_pos
  const redemptionProviders = getUniqueRedemptionProviders(merchant)

  const [currentCard, setCurrentCard] = useState<Card>(() => {
    if (!isDraft) return Card.CUSTOMER_SUPPORT
    if (!description) return Card.REWARD_DESCRIPTION
    // Delivery-discount templates skip the online and in-store cards entirely
    // (see cardOrder). On edit, jump straight to the last card in that
    // order so Number of Uses / Customer Support open by default and the
    // Activate/Send-a-test gate (isLastCard) can be reached.
    if (isDeliveryDiscount) {
      return canAccessFeedback ? Card.CUSTOMER_SUPPORT : Card.ITEM_VALUE
    }
    let isOnlineRedemptionFilled = true
    if (availableOnline) {
      if (key === 'hidden_menu') {
        isOnlineRedemptionFilled = !isEmpty(hiddenMenuKey?.[0])
      } else if (subtype === 'bogo/item') {
        isOnlineRedemptionFilled =
          !!discountedProductIds && !!requiredProductIds
      } else if (subtype !== 'purchase') {
        isOnlineRedemptionFilled = !!discountedProductIds
      }
    }

    if (!isOnlineRedemptionFilled) return Card.ONLINE_REDEMPTION
    // If this code is reached we are editing the template
    // If a merchant does not have multiple POS, we can't know if instore card is filled
    // as it has a default value. So simply start from customer support card.
    if (multiplePosSystems) return Card.INSTORE_REDEMPTION
    if (canAccessFeedback) return Card.CUSTOMER_SUPPORT
    return isHiddenMenu ? Card.CUSTOMER_SUPPORT : Card.INSTORE_REDEMPTION
  })
  const [isPublished, setIsPublished] = useState(false)
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false)
  const [isSendTestModalOpen, setIsSendTestModalOpen] = useState(false)
  const [isLocationsPosModalOpen, setIsLocationsPosModalOpen] = useState(false)
  const [isChangeActiveTemplateModalOpen, setIsChangeActiveTemplateModalOpen] =
    useState(false)

  let isAllValid = isValidForm
  if (currentCard >= Card.INSTORE_REDEMPTION && !isDeliveryDiscount) {
    isAllValid =
      isValidForm &&
      (isValidCouponPool || !!merchant?.supports_loyalty_integration)
  }

  // Source of truth for the wizard sequence; kept in sync with the cards
  // rendered below and the isLastCard check. Memoized for a stable ref.
  // Delivery-discount templates skip the online and in-store cards entirely.
  const cardOrder: Card[] = React.useMemo(() => {
    const order: Card[] = [Card.REWARD_DESCRIPTION, Card.REWARD_APPEARANCE]
    if (!isHiddenMenu) {
      order.push(Card.ITEM_VALUE)
      if (
        typeItem?.types.includes('manual/fixed_price') &&
        isFreeVariantKey(typeItem.key)
      ) {
        order.push(Card.DISCOUNT_CONFIG)
      }
    }
    if (
      merchant?.ordering_enabled &&
      !isForWholePurchase &&
      !isGoldenTicket &&
      !isDeliveryDiscount
    ) {
      order.push(Card.ONLINE_REDEMPTION)
    }
    if (!merchant?.online_only && !isDeliveryDiscount) {
      order.push(Card.INSTORE_REDEMPTION)
    }
    if (showMinimumSpend) {
      order.push(Card.MINIMUM_SPEND)
    }
    if (showMaximumDiscount) {
      order.push(Card.MAXIMUM_DISCOUNT)
    }
    if (multiUseRewardsEnabled) {
      order.push(Card.MULTI_USE_REWARDS)
    }
    if (canAccessFeedback) {
      order.push(Card.CUSTOMER_SUPPORT)
    }
    return order
  }, [
    isHiddenMenu,
    typeItem,
    merchant?.ordering_enabled,
    isForWholePurchase,
    isGoldenTicket,
    isDeliveryDiscount,
    merchant?.online_only,
    showMinimumSpend,
    showMaximumDiscount,
    multiUseRewardsEnabled,
    canAccessFeedback,
  ])

  const isLastCard = currentCard === cardOrder[cardOrder.length - 1]
  const isComplete = isLastCard && isAllValid

  const handleCurrentCard = useCallback(
    (updatedData?: {
      availableOnline?: boolean
      availableInstore?: boolean
    }) => {
      const currentAvailableOnline =
        updatedData?.availableOnline || availableOnline
      const currentAvailableInstore =
        updatedData?.availableInstore || availableInstore

      // move to instore-redemption card when online-redemption is disabled
      if (
        currentCard === Card.ONLINE_REDEMPTION &&
        ((!currentAvailableOnline && !merchant?.online_only) ||
          !merchant?.ordering_enabled)
      ) {
        setCurrentCard(Card.INSTORE_REDEMPTION)
      }

      // Advance to the next card in order when in-store is disabled.
      if (
        currentCard === Card.INSTORE_REDEMPTION &&
        currentAvailableInstore === false
      ) {
        const nextCard =
          cardOrder[cardOrder.indexOf(Card.INSTORE_REDEMPTION) + 1]
        if (nextCard !== undefined) setCurrentCard(nextCard)
      }
    },
    [
      availableOnline,
      availableInstore,
      merchant?.online_only,
      merchant?.ordering_enabled,
      cardOrder,
      currentCard,
    ]
  )

  function handleFormChange(newFormModel: FormModel, isChanged: boolean) {
    const { availableOnline, availableInstore } = newFormModel

    handleCurrentCard({ availableOnline, availableInstore })

    // this method is triggered by formsy when new inputs are attached
    // to the form. So I'm filtering them here before updating the model.
    if (
      isChanged ||
      (newFormModel.longDescription === null &&
        longDescription !== newFormModel.longDescription)
    ) {
      // Guard against cascading Formsy onChange calls: onFormChange →
      // setFormModel → React batched re-render → Formsy fires onChange
      // again. setTimeout(0) keeps the guard locked through React's
      // batched updates and releases it for the next event-loop task.
      if (isProcessingChange.current) return
      isProcessingChange.current = true
      onFormChange(newFormModel, isChanged)
      setTimeout(() => {
        isProcessingChange.current = false
      }, 0)
    }
  }

  // validate coupon pools here as they are not validated within the form
  useEffect(() => {
    let isValid = true

    handleCurrentCard()

    if (
      availableInstore &&
      instoreRedemptionType === 'coupon_codes' &&
      couponCodes.generationType === 'variable' &&
      (isDraft || !isEmpty(fileUploads))
    ) {
      // first, check if we have an existing pool selected
      if (!!selectedCouponPools?.length) {
        isValid = true
      } else {
        // form is valid if we have at least one finalized
        // and none processing file uploads.
        isValid =
          fileUploads.some(u => u.state === 'finalized') &&
          !fileUploads.some(u => u.state === 'processing')
      }
    }
    setIsValidCouponPool(isValid)
  }, [
    availableInstore,
    instoreRedemptionType,
    handleCurrentCard,
    couponCodes,
    fileUploads,
    isValidCouponPool,
    setIsValidCouponPool,
    isDraft,
    selectedCouponPools,
  ])
  if (!typeItem) return null

  if (isPublished) {
    return <Success />
  }

  const handleNext = () => {
    const index = cardOrder.indexOf(currentCard)
    if (index < cardOrder.length - 1) setCurrentCard(cardOrder[index + 1])
  }

  const handleSaveAndExit = async () => {
    if (shouldShowChangeActiveTemplateModal()) {
      setIsChangeActiveTemplateModalOpen(true)
    } else {
      await saveAndExit()
    }
  }

  const handleActivate = async () => {
    await saveForm()
    setIsActivateModalOpen(true)
  }

  const saveAndExit = async () => {
    const success = await saveForm()
    if (success) {
      onSuccessExit()
    }
  }

  const shouldShowChangeActiveTemplateModal = () => {
    return isDirty && isActive
  }

  const helpArticles = getHelp(typeItem.key, freeModifierRewardsEnabled)

  const renderItemValueSection = () => {
    if (isHiddenMenu) return null

    const cardProps = {
      isOpen: currentCard >= Card.ITEM_VALUE,
      isCurrent: currentCard === Card.ITEM_VALUE,
      isValidForm: isAllValid,
      onNext: handleNext,
    }

    if (isDeliveryDiscount) {
      return (
        <DeliveryDiscountCard
          isDisabled={!isDraft}
          onFormChange={handleFormChange}
          {...cardProps}
        />
      )
    }

    if (typeItem.types.includes('manual/item') || isGoldenTicket) {
      return <ItemValueCard {...cardProps} />
    }

    if (typeItem.types.includes('manual/fixed_price')) {
      const isFreeVariant = isFreeVariantKey(typeItem.key)
      return (
        <FixedPriceCard
          isDisabled={!isDraft}
          onFormChange={handleFormChange}
          discountConfigIsOpen={
            isFreeVariant && currentCard >= Card.DISCOUNT_CONFIG
          }
          discountConfigIsCurrent={
            isFreeVariant && currentCard === Card.DISCOUNT_CONFIG
          }
          {...cardProps}
        />
      )
    }

    return <DiscountCard isDisabled={!isDraft} {...cardProps} />
  }

  return (
    <>
      <ActivateModal
        templateId={templateId}
        name={name}
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        onActivate={() => setIsPublished(true)}
      />
      <LocationPosModal
        locations={locations}
        isOpen={isLocationsPosModalOpen}
        onClose={() => setIsLocationsPosModalOpen(false)}
      />
      <SendTestModal
        templateId={templateId}
        isOpen={isSendTestModalOpen}
        isLoading={isLoading}
        onClose={() => setIsSendTestModalOpen(false)}
        onBeforeSend={async () => {
          // save template to make sure the data synced with the backend
          await saveForm()
        }}
        onSuccess={() => setIsSendTestModalOpen(false)}
        showHiddenMenuInfo={key === 'hidden_menu'}
      />
      <ChangeActiveTemplateModal
        isOpen={isChangeActiveTemplateModalOpen}
        onClose={() => setIsChangeActiveTemplateModalOpen(false)}
        onOkay={async () => {
          await saveAndExit()
          setIsChangeActiveTemplateModalOpen(false)
        }}
        multiUseSettings={
          formModel.multiUseRewards.usageType === 'multiple_times'
            ? formModel.multiUseRewards
            : undefined
        }
        hasMultiUseChanges={hasMultiUseChanges}
      />
      <TopBar
        step="configuration"
        templateId={templateId}
        hideTypeStep={!isDraft}
        onStepSelected={onStepSelected}
      >
        <Button
          className="mr-m"
          kind="secondary_outline"
          disabled={!isDraft && !isAllValid}
          isLoading={isLoading}
          onClick={handleSaveAndExit}
        >
          {t('save_exit')}
        </Button>
        <Button
          className="mr-m"
          kind="secondary_outline"
          disabled={!isComplete}
          isLoading={isLoading}
          onClick={() => setIsSendTestModalOpen(true)}
        >
          {t('send_a_test')}
        </Button>
        <Switch condition={isDraft}>
          <Button
            className={isMobile ? 'mt-xs' : ''}
            kind="secondary_outline"
            disabled={!isComplete}
            isLoading={isLoading}
            onClick={handleActivate}
          >
            {t('activate')}
          </Button>
        </Switch>
      </TopBar>
      <Container>
        <Formsy
          // @ts-ignore
          ref={formRef}
          className="text-left form-group form-group-lg"
          onValid={() => setIsValidForm(true)}
          onInvalid={() => setIsValidForm(false)}
          onChange={handleFormChange}
        >
          <Row>
            <Col className="my-xl">
              <EditableName
                name={formModel.name}
                styleSize="large"
                type="redeem"
              />
              <p className="body-text-2 grey-70">
                {I18n.t(`redeem_manager.types.${typeItem.key}.title`)}
              </p>
            </Col>
          </Row>
          <Row>
            <Col lg={7}>
              <DescriptionCard
                isDisabled={!isDraft && formModel.availableOnline}
                description={description}
                longDescription={longDescription}
                example={I18n.t(
                  `redeem_manager.types.${typeItem.key}.description_example`
                )}
                isCurrent={currentCard === Card.REWARD_DESCRIPTION}
                isValidForm={isAllValid}
                onNext={handleNext}
              />
              <AppearanceCard
                description={description}
                isEnabled
                isCurrent={currentCard === Card.REWARD_APPEARANCE}
                isOpen={currentCard >= Card.REWARD_APPEARANCE}
                onNext={handleNext}
                templateId={templateId}
              />
              {renderItemValueSection()}
              <Switch condition={!isDeliveryDiscount}>
                <OnlineRedemptionCard
                  templateId={templateId}
                  isDraft={isDraft}
                  isEnabled={availableOnline && !isGoldenTicket}
                  isOpen={currentCard >= Card.ONLINE_REDEMPTION}
                  isCurrent={currentCard === Card.ONLINE_REDEMPTION}
                  isValidForm={isAllValid}
                  hideToggle={isGoldenTicket}
                  onNext={handleNext}
                />
                <Switch
                  // Fallback to showing only a single card if it's an experience reward
                  condition={!isExperience && multiplePosSystems}
                  fallback={
                    <InstoreRedemptionCard
                      templateId={templateId}
                      isEnabled={availableInstore}
                      isOpen={currentCard >= Card.INSTORE_REDEMPTION}
                      isCurrent={currentCard === Card.INSTORE_REDEMPTION}
                      isDraft={isDraft}
                      isValidForm={isAllValid}
                      onNext={handleNext}
                    />
                  }
                >
                  <MultipleRedemptionProviders
                    redemptionProviders={redemptionProviders}
                    isEnabled={availableInstore}
                    isOpen={currentCard >= Card.INSTORE_REDEMPTION}
                    isCurrent={currentCard === Card.INSTORE_REDEMPTION}
                    isDraft={isDraft}
                    isValidForm={isAllValid}
                    onNext={handleNext}
                    setLocationModalOpen={setIsLocationsPosModalOpen}
                    setConfigureProviderStep={setConfigureProviderStep}
                    setSelectedProvider={setSelectedProvider}
                  />
                </Switch>
              </Switch>
              <Switch condition={showMinimumSpend}>
                <MinimumSpendCard
                  isOpen={currentCard >= Card.MINIMUM_SPEND}
                  isCurrent={currentCard === Card.MINIMUM_SPEND}
                  onNext={handleNext}
                />
              </Switch>
              <Switch condition={showMaximumDiscount}>
                <MaximumDiscountCard
                  isOpen={currentCard >= Card.MAXIMUM_DISCOUNT}
                  isCurrent={currentCard === Card.MAXIMUM_DISCOUNT}
                  onNext={handleNext}
                />
              </Switch>
              <Switch condition={multiUseRewardsEnabled}>
                <MultiUseRewardsCard
                  isCurrent={currentCard === Card.MULTI_USE_REWARDS}
                  isOpen={currentCard >= Card.MULTI_USE_REWARDS}
                  onNext={handleNext}
                  onFormChange={handleFormChange}
                  settings={formModel.multiUseRewards}
                  discount={formModel.discount}
                />
              </Switch>
              <Switch condition={canAccessFeedback}>
                <CustomerSupportCard
                  isEnabled={availableCustomerSupport && !isExperience}
                  isOpen={currentCard >= Card.CUSTOMER_SUPPORT}
                  hideToggle={isExperience}
                />
              </Switch>
              <Restrictions />
              <Help
                articles={helpArticles}
                title={I18n.t(
                  'redeem_manager.builder.steps.configuration.help.title'
                )}
              />
              <Switch condition={isComplete}>
                <Button
                  className="w-100"
                  isLoading={isLoading}
                  onClick={() => setIsSendTestModalOpen(true)}
                >
                  {t('send_a_test')}
                </Button>
              </Switch>
            </Col>
            <Col
              className="d-sm-none d-md-block"
              lg={{
                span: 4,
                offset: 1,
              }}
            >
              <Preview
                currentCard={currentCard}
                isExperience={isExperience}
                templateId={templateId}
              />
            </Col>
          </Row>
        </Formsy>
      </Container>
    </>
  )
}

export default ConfigurationStep
